import {
  Question,
  type Questionnaire,
  type QuestionnairePage,
} from '@/lib/questionnaires/Questionnaire'
import { InferType, object, string } from 'yup'
import { mustAgree } from '@/shared/forms/YupValidators'
import { BooleanYesNoTransformer } from './Prescription'
import { GeneralConsultationQuestionnaire } from '@/lib/apis/types'

export const ConsultationQuestionnaire: Questionnaire = {
  pages: [
    {
      questions: [
        Question.freeText(
          'description',
          'Please provide a detailed description of the reason for your consultation.',
        ),
        Question.yesNo('isForSelf', 'Is this consultation for yourself?'),
        Question.yesNo('isOlderThan18', 'Are you at least 18 years old?'),
        Question.yesNo(
          'hasSignificantMedicalConditions',
          'Do you have any significant current and/or past medical conditions?',
        ),
        Question.freeText(
          'significantMedicalConditions',
          'Please list your medical conditions in details.',
        ),
        Question.yesNo(
          'isTakingRegularMedications',
          'Are you taking any regular medications?',
        ),
        Question.freeText(
          'regularMedications',
          'Please list your regular medications in details.',
        ),
        Question.yesNo(
          'hasAllergies',
          'Are you allergic to anything such as medicine, food, etc?',
        ),
        Question.freeText(
          'allergies',
          'Please list your allergies in details.',
        ),
      ],
    },
    {
      questions: [
        Question.yesNo(
          'allowsAutoNoteTakingSoftware',
          'Your practitioner may use note-taking software during your consultation. Are you happy for them to do that?',
        ),
        Question.yesNo('isInAustralia', 'Are you in Australia?'),
        Question.yesNo(
          'tncAccepted',
          'Have you read and accept the terms and conditions?',
        ),
      ],
    },
  ],
}

export const ConsultationQuestionnaireValidator = object({
  description: string()
    .default('')
    .required('You must provide your reason for your consultation.'),
  isForSelf: string()
    .default('')
    .transform(BooleanYesNoTransformer)
    .oneOf(
      ['Yes', 'No'] as const,
      'You must select an answer for this question.',
    )
    .test(mustAgree('Sorry, patients must submit the request for themselves.')),
  isOlderThan18: string()
    .default('')
    .transform(BooleanYesNoTransformer)
    .oneOf(
      ['Yes', 'No'] as const,
      'You must select an answer for this question.',
    )
    .test(mustAgree('Sorry, you must be at least 18 to use our services.')),
  hasSignificantMedicalConditions: string()
    .default('')
    .transform(BooleanYesNoTransformer)
    .oneOf(
      ['Yes', 'No'] as const,
      'You must select an answer for this question.',
    ),
  significantMedicalConditions: string()
    .default('')
    .test({
      test: (value, context) => {
        if (
          context.parent.hasSignificantMedicalConditions === 'Yes' &&
          value.trim().length === 0
        )
          return context.createError({ message: 'You need to answer this.' })
        return true
      },
    }),
  isTakingRegularMedications: string()
    .default('')
    .transform(BooleanYesNoTransformer)
    .oneOf(
      ['Yes', 'No'] as const,
      'You must select an answer for this question.',
    ),
  regularMedications: string()
    .default('')
    .test({
      test: (value, context) => {
        if (
          context.parent.isTakingRegularMedications === 'Yes' &&
          value.trim().length === 0
        )
          return context.createError({ message: 'You need to answer this.' })
        return true
      },
    }),
  hasAllergies: string()
    .default('')
    .transform(BooleanYesNoTransformer)
    .oneOf(
      ['Yes', 'No'] as const,
      'You must select an answer for this question.',
    ),
  allergies: string()
    .default('')
    .test({
      test: (value, context) => {
        if (context.parent.hasAllergies === 'Yes' && value.trim().length === 0)
          return context.createError({ message: 'You need to answer this.' })
        return true
      },
    }),
  isInAustralia: string()
    .default('')
    .transform(BooleanYesNoTransformer)
    .oneOf(
      ['Yes', 'No'] as const,
      'You must select an answer for this question.',
    )
    .test(
      mustAgree('Sorry, you need to be in Australia to start a consultation.'),
    ),
  allowsAutoNoteTakingSoftware: string()
    .default('')
    .transform(BooleanYesNoTransformer)
    .oneOf(
      ['Yes', 'No'] as const,
      'You must select an answer for this question.',
    ),
  tncAccepted: string()
    .default('')
    .transform(BooleanYesNoTransformer)
    .oneOf(
      ['Yes', 'No'] as const,
      'You must select an answer for this question.',
    )
    .test(mustAgree('Sorry, you need to accept this to start a consultation.')),
})

export interface ConsultationQuestionnaireType extends InferType<
  typeof ConsultationQuestionnaireValidator
> {}

export function createConsultationQuestions(
  page: number,
  values: ConsultationQuestionnaireType,
): QuestionnairePage {
  const hiddenQuestions = new Set<string>()

  if (values.hasSignificantMedicalConditions !== 'Yes')
    hiddenQuestions.add('significantMedicalConditions')
  if (values.isTakingRegularMedications !== 'Yes')
    hiddenQuestions.add('regularMedications')
  if (values.hasAllergies !== 'Yes') hiddenQuestions.add('allergies')

  const defaultQuestions = ConsultationQuestionnaire.pages[page] as {
    questions: Array<Question>
  }
  const filteredQuestions = defaultQuestions.questions.filter(
    (question) => !('key' in question && hiddenQuestions.has(question.key)),
  )

  return {
    questions: filteredQuestions,
  }
}

export function generalConsultationFormToDto(
  values: ConsultationQuestionnaireType,
): GeneralConsultationQuestionnaire {
  return {
    description: values.description,
    hasSignificantMedicalConditions:
      values.hasSignificantMedicalConditions === 'Yes',
    significantMedicalConditions: values.significantMedicalConditions,
    isTakingRegularMedications: values.isTakingRegularMedications === 'Yes',
    regularMedications: values.regularMedications,
    hasAllergies: values.hasAllergies === 'Yes',
    allergies: values.allergies,
    isInAustralia: values.isInAustralia === 'Yes',
    tncAccepted: values.tncAccepted === 'Yes',
  }
}
